import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * Mobile browsers — iOS Safari in particular — will not honor the `autoplay`
 * attribute reliably once the `src` is assigned dynamically. To play inline and
 * unprompted they require the `muted` *property* (not just the attribute) to be
 * set, an explicit `play()` call, and a graceful retry path for the cases where
 * the browser still refuses (Low Power Mode, a backgrounded tab, reduced-motion).
 * This component drives all of that, and defers loading until the video is in
 * view so off-screen instances don't decode on mobile data.
 *
 * When playback genuinely can't start (Low Power / Low Data Mode disable autoplay
 * at the OS level, and no site can override them), the component flags its state so
 * the poster image stays layered over the paused video — visitors see a clean still
 * frame instead of a frozen video with the browser's play glyph. The poster fades
 * out the moment playback is confirmed (`data-playback-state="playing"`).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** Whether the sources have been wired up and `load()` has been called. */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    // Respect the user's reduced-motion preference. The accompanying stylesheet
    // hides the video in this case, so there is nothing to play.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { videoElement } = this.refs;

    // iOS requires the muted *property*, not just the attribute, to autoplay inline.
    videoElement.muted = true;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Confirmed playback fades the poster out; an unrecoverable pause brings it back.
    videoElement.addEventListener('playing', () => this.#setState('playing'), { signal });

    // Load and play only once the background scrolls into view. The hero (and any
    // above-the-fold instance) intersects immediately, so it starts right away.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#load();
            this.#play();
          } else if (this.#loaded) {
            // Pause off-screen to save battery and avoid decode glitches on mobile.
            videoElement.pause();
          }
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);

    // If autoplay was blocked, start playback on the first user interaction or
    // when the tab returns to the foreground.
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    document.addEventListener('pointerdown', this.#handleInteraction, { signal });
    document.addEventListener('touchstart', this.#handleInteraction, { signal, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  /** Wire the deferred sources onto the `<video>` and begin buffering (once). */
  #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { videoSources, videoElement } = this.refs;

    // The hero ships its sources with a real `src` already in the HTML (see
    // background-media.liquid); only the lazy, below-the-fold path needs the
    // deferred `data-video-source` promoted, followed by a load() to pick it up.
    let injectedSource = false;
    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) {
        source.setAttribute('src', videoSource);
        injectedSource = true;
      }
    }

    // iOS rejects a play() issued before the media has data, so also (re)attempt
    // playback the moment the element reports it can play. `#play` is guarded, so
    // these repeated signals are harmless.
    videoElement.addEventListener('loadeddata', this.#play, { signal: this.#controller?.signal });
    videoElement.addEventListener('canplay', this.#play, { signal: this.#controller?.signal });

    // Re-running load() on the eager hero would reset its in-flight native autoplay,
    // so only call it when we actually injected a deferred source.
    if (injectedSource) videoElement.load();
  }

  /**
   * Attempt playback. A rejection means the browser blocked autoplay (e.g. iOS
   * Low Power / Low Data Mode) — flag it so the poster image stays in front of the
   * paused video instead of the browser's play glyph. The `playing` event clears
   * the flag if a later retry succeeds.
   */
  #play = () => {
    const { videoElement } = this.refs;
    if (!videoElement.paused) return;

    const attempt = videoElement.play();
    if (attempt) attempt.catch(() => this.#setState('blocked'));
  };

  /** @param {'playing' | 'blocked'} state */
  #setState(state) {
    this.dataset.playbackState = state;
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#play();
  };

  #handleInteraction = () => this.#play();
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
